import React, { useState, useRef } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Paper,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Delete as DeleteIcon,
  CameraAlt as CameraIcon
} from '@mui/icons-material';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import { motion, AnimatePresence } from 'motion/react';
import { getApiUrl } from '../apiUrl';

const AddClearanceEvent = ({ store, onClose, onEventCreated }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const API_URL = getApiUrl();

  // Helper to convert R2.dev URLs or R2 paths to proxy URLs
  const getImageUrl = (url) => {
    if (!url) return '';
    
    // If it's already a proxy URL, use as-is
    if (url.includes('/api/images/')) {
      return url;
    }
    
    // If it's an R2.dev URL, extract the key and convert to proxy
    if (url.includes('.r2.dev/')) {
      const keyMatch = url.match(/r2\.dev\/(.+)$/);
      if (keyMatch) {
        return `${API_URL}/api/images/${encodeURIComponent(keyMatch[1])}`;
      }
    }
    
    // If it's a Cloudflare UI path format (fire-sales-images/events/xxx.png)
    // or just the key (events/xxx.png), use it directly
    if (url.includes('events/') || url.includes('profiles/')) {
      const keyMatch = url.match(/(?:fire-sales-images\/)?((?:events|profiles)\/.+)$/);
      if (keyMatch) {
        return `${API_URL}/api/images/${encodeURIComponent(keyMatch[1])}`;
      }
    }
    
    // Otherwise return as-is (might be base64 or other format)
    return url;
  };

  const uploadToR2 = async (file) => {
    try {
      // Light compression on client side (server will do final optimization)
      // Keep quality high for zooming - server will handle final optimization
      const options = {
        maxSizeMB: 5, // Allow larger files, server will optimize
        maxWidthOrHeight: 4000, // Match server max
        useWebWorker: true
      };
      
      const compressedFile = await imageCompression(file, options);
      
      // Upload to server endpoint which handles R2 upload
      const formData = new FormData();
      formData.append('image', compressedFile, file.name);

      const response = await axios.post(
        `${API_URL}/api/upload-image`,
        formData
      );

      if (response.data.success) {
        return {
          url: response.data.photo.url,
          key: response.data.photo.key,
          thumbUrl: response.data.photo.url, // R2 URL, can add thumbnail later if needed
          uploadedAt: response.data.photo.uploadedAt
        };
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading to R2:', error);
      throw error;
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhotos(true);
    try {
      const uploadPromises = files.map(file => uploadToR2(file));
      const uploadedPhotos = await Promise.all(uploadPromises);
      setPhotos([...photos, ...uploadedPhotos]);
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert('Error uploading photos. Please try again.');
    } finally {
      setUploadingPhotos(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !store) return;
    if (photos.length === 0) {
      alert('Please add at least one photo of the clearance event');
      return;
    }

    setSubmitting(true);
    try {
      // If store._id exists, use it. Otherwise, send full store data to create it
      const requestBody = {
        title: title.trim(),
        description: description.trim() || undefined,
        photos: photos.map(p => ({ 
          url: p.url, 
          key: p.key, // Include R2 key for deletion
          uploadedAt: p.uploadedAt 
        }))
      };

      if (store._id) {
        // Store exists in database
        requestBody.storeId = store._id;
      } else {
        // Store doesn't exist - send store data to create it
        requestBody.storeData = {
          name: store.name,
          location: store.location,
          category: store.category
        };
      }

      const response = await axios.post(
        `${API_URL}/api/events`,
        requestBody,
        { withCredentials: true }
      );

      if (response.status === 201) {
        if (onEventCreated) {
          onEventCreated(response.data);
        }
        onClose();
      }
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Error creating event: ' + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#001623',
          color: 'white',
          flexShrink: 0
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            New Event
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.75rem' }}>
            {store?.name}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          backgroundColor: '#f5f5f5'
        }}
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Photo Upload Section - Prominent */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: 'white',
              borderRadius: 2,
              border: '2px dashed',
              borderColor: photos.length > 0 ? 'transparent' : '#e0e0e0',
              transition: 'all 0.2s'
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#333' }}>
              Photos *
            </Typography>
            
            {photos.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 1.5,
                  mb: 2
                }}
              >
                <AnimatePresence>
                  {photos.map((photo, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      style={{ position: 'relative', aspectRatio: '1' }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: '100%',
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <img
                          src={getImageUrl(photo.thumbUrl || photo.url)}
                          alt={`Upload ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removePhoto(index)}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.8)'
                            },
                            width: 28,
                            height: 28
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </Box>
            )}

            {uploadingPhotos && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                  Uploading...
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
                disabled={uploadingPhotos}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
                disabled={uploadingPhotos}
              />
              
              <Button
                variant="outlined"
                startIcon={<AddPhotoIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhotos}
                fullWidth
                sx={{
                  py: 1.5,
                  borderColor: '#202E40',
                  color: '#202E40',
                  '&:hover': {
                    borderColor: '#202E40',
                    backgroundColor: 'rgba(32, 46, 64, 0.04)'
                  }
                }}
              >
                {photos.length === 0 ? 'Add Photos' : 'Add More'}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<CameraIcon />}
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploadingPhotos}
                sx={{
                  py: 1.5,
                  borderColor: '#202E40',
                  color: '#202E40',
                  minWidth: 'auto',
                  px: 2,
                  '&:hover': {
                    borderColor: '#202E40',
                    backgroundColor: 'rgba(32, 46, 64, 0.04)'
                  }
                }}
              >
                Camera
              </Button>
            </Box>
            
            {photos.length === 0 && (
              <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary', textAlign: 'center' }}>
                Add photos of the clearance items (e.g., yellow tags, discount aisles)
              </Typography>
            )}
          </Paper>

          {/* Title */}
          <TextField
            required
            label="Event Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Electronics Clearance - 35-60% Off"
            variant="outlined"
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                borderRadius: 2
              }
            }}
          />

          {/* Description */}
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add details about the clearance event..."
            variant="outlined"
            multiline
            rows={3}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                borderRadius: 2
              }
            }}
          />
        </form>
      </Box>

      {/* Fixed Footer with Submit Button */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'white',
          display: 'flex',
          gap: 1.5
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={submitting}
          sx={{
            flex: 1,
            py: 1.5,
            borderRadius: 2,
            borderColor: '#e0e0e0',
            color: '#666',
            '&:hover': {
              borderColor: '#ccc',
              backgroundColor: 'rgba(0, 0, 0, 0.02)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || uploadingPhotos || !title.trim() || photos.length === 0}
          sx={{
            flex: 2,
            py: 1.5,
            borderRadius: 2,
            backgroundColor: '#202E40',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#2a3d54'
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#999'
            }
          }}
        >
          {submitting ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
              Posting...
            </>
          ) : (
            'Post Event'
          )}
        </Button>
      </Box>
    </motion.div>
  );
};

export default AddClearanceEvent;
